import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Radio, RefreshCw, ShieldAlert, ShieldCheck, WifiOff } from "lucide-react";
import CrudPage from "@/components/CrudPage";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const statusTag = (s) =>
  s === "online" ? <span className="sbc-tag tag-online"><span className="led led-green" /> conectado</span>
    : s === "degraded" ? <span className="sbc-tag tag-warn"><span className="led led-amber" /> parcial</span>
    : <span className="sbc-tag tag-offline"><span className="led led-gray" /> offline</span>;

const loginTag = (r) => {
  if (!r.registration_required) return <span className="sbc-tag tag-online">sem login</span>;
  if (r.registration_status === "registered") return <span className="sbc-tag tag-online">registrado</span>;
  if (r.registration_status === "trying") return <span className="sbc-tag tag-warn">registrando</span>;
  return <span className="sbc-tag tag-offline">login falhou</span>;
};

function MonitorCard({ label, value, helper, tone = "neutral", icon: Icon }) {
  const tones = {
    neutral: "text-[color:var(--text-primary)]",
    green: "text-[color:var(--accent-green)]",
    amber: "text-[color:var(--accent-amber)]",
    red: "text-[color:var(--accent-red)]",
  };
  return (
    <div className="rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
          <div className={`mt-2 text-2xl font-mono font-semibold ${tones[tone]}`}>{value}</div>
          <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">{helper}</div>
        </div>
        {Icon ? <Icon size={16} className="mt-1 text-[color:var(--text-secondary)]" /> : null}
      </div>
    </div>
  );
}

function FailureList({ title, rows, tone = "red" }) {
  const toneClass = tone === "amber" ? "text-[color:var(--accent-amber)]" : "text-[color:var(--accent-red)]";
  return (
    <div className="rounded-sm border border-[color:var(--border-default)]">
      <div className="border-b border-[color:var(--border-default)] px-3 py-2 text-xs font-semibold">{title}</div>
      <div className="max-h-52 overflow-auto">
        {!rows?.length ? (
          <div className="px-3 py-4 text-xs text-[color:var(--text-muted)]">Sem alertas recentes.</div>
        ) : (
          rows.map((row, index) => (
            <div key={`${row.id || row.nome || "item"}-${index}`} className="flex items-start justify-between gap-3 border-b border-[color:var(--border-default)] px-3 py-2 last:border-b-0">
              <div className="min-w-0">
                <div className="text-sm font-medium">{row.nome || row.id || "Operadora"}</div>
                <div className="text-[11px] font-mono text-[color:var(--text-muted)] break-all">{row.host || "-"}</div>
              </div>
              <div className={`shrink-0 text-right text-[11px] font-mono ${toneClass}`}>
                <div>{row.hangup_cause || row.registration_status || row.status || "falha"}</div>
                <div className="text-[color:var(--text-muted)]">{fmtDate(row.created_at || row.updated_at || row.last_seen_at)}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function Troncos() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [loadingMonitor, setLoadingMonitor] = useState(true);
  const [monitorError, setMonitorError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    const loadHealth = async () => {
      if (firstLoadRef.current) {
        setLoadingMonitor(true);
      }
      try {
        const { data } = await api.get("/health", { params: { _ts: Date.now() } });
        if (!cancelled) {
          setHealth(data);
          setMonitorError("");
          firstLoadRef.current = false;
        }
      } catch (err) {
        if (!cancelled) {
          setMonitorError(err?.response?.data?.detail || "Falha ao carregar monitoramento das operadoras.");
          firstLoadRef.current = false;
        }
      } finally {
        if (!cancelled) {
          setLoadingMonitor(false);
        }
      }
    };
    loadHealth();
    const intervalId = setInterval(loadHealth, 5000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [refreshTick]);

  const operadorasHealth = health?.operadoras || {};
  const failureRows = useMemo(() => (health?.last_failures?.operadoras || []).slice(0, 8), [health]);
  const problematicRows = useMemo(
    () => (operadorasHealth.items || []).filter((row) => row.status === "offline" || row.status === "degraded"),
    [operadorasHealth],
  );

  const topContent = (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Monitoramento em tempo real</div>
          <div className="text-sm font-semibold">Serviços das operadoras</div>
          <div className="text-[11px] text-[color:var(--text-muted)] mt-1">
            Acompanhe conectividade, registro SIP e alertas das operadoras configuradas no SBC.
          </div>
        </div>
        <button className="sbc-btn" type="button" onClick={() => setRefreshTick((value) => value + 1)}>
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {monitorError ? (
        <div className="rounded-sm border border-[rgba(239,68,68,0.30)] bg-[rgba(239,68,68,0.08)] px-3 py-2 text-xs font-mono text-[color:var(--accent-red)]">
          {monitorError}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MonitorCard
          label="Operadoras online"
          value={`${operadorasHealth.online || 0}/${operadorasHealth.enabled || operadorasHealth.total || 0}`}
          helper={`${operadorasHealth.total || 0} cadastradas`}
          tone={(operadorasHealth.offline || 0) > 0 ? "amber" : "green"}
          icon={ShieldCheck}
        />
        <MonitorCard
          label="Degradadas"
          value={operadorasHealth.degraded || 0}
          helper="Conectadas com registro ou saúde parcial"
          tone={(operadorasHealth.degraded || 0) > 0 ? "amber" : "neutral"}
          icon={AlertTriangle}
        />
        <MonitorCard
          label="Offline"
          value={operadorasHealth.offline || 0}
          helper="Sem entrega confiável do serviço"
          tone={(operadorasHealth.offline || 0) > 0 ? "red" : "neutral"}
          icon={WifiOff}
        />
        <MonitorCard
          label="Logins SIP"
          value={operadorasHealth.login_required ? `${operadorasHealth.registered || 0}/${operadorasHealth.login_required}` : "N/A"}
          helper="Troncos que exigem autenticação"
          tone={(operadorasHealth.registered || 0) < (operadorasHealth.login_required || 0) ? "amber" : "green"}
          icon={Radio}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <FailureList title="Operadoras com atenção agora" rows={problematicRows} tone="amber" />
        <FailureList title="Últimas falhas detectadas" rows={failureRows} tone="red" />
      </div>

      <div className="text-[11px] text-[color:var(--text-muted)] font-mono">
        {loadingMonitor ? "Atualizando monitoramento..." : `Atualizado em ${fmtDate(new Date().toISOString())}`}
      </div>
    </div>
  );

  return (
    <CrudPage
      title="Troncos SIP"
      subtitle="Trunks de saída/entrada"
      endpoint="/operadoras"
      testidPrefix="troncos"
      topContent={topContent}
      headers={["Nome", "Host", "Porta", "Proto", "Usuário", "Codec", "Canais", "Saída", "Escopo", "Conectividade", "Login"]}
      canEdit={(r) => !r.read_only}
      canDelete={(r) => !r.read_only}
      renderRow={(r) => {
        const protectedUniversal = r.universal && user?.role !== "superadmin";
        return <>
          <td className="font-medium">{r.nome}</td>
          <td className="font-mono">{protectedUniversal ? <span className="text-[color:var(--text-muted)]">Protegido</span> : r.host}</td>
          <td className="font-mono">{r.porta}</td>
          <td className="font-mono uppercase text-[color:var(--text-secondary)]">{r.protocolo}</td>
          <td className="font-mono">{protectedUniversal ? "-" : (r.usuario || "-")}</td>
          <td className="font-mono text-xs">{r.codec}</td>
          <td className="font-mono">{r.canais_max}</td>
          <td className="font-mono text-xs">
            {(r.remover_digitos_saida || r.remover_prefixo_destino || r.incluir_digitos_saida || r.adicionar_prefixo_destino || r.prefixo_saida) ? (
              <span>
                -{r.remover_digitos_saida || r.remover_prefixo_destino || "-"} / +{r.incluir_digitos_saida || r.adicionar_prefixo_destino || r.prefixo_saida || "-"}
              </span>
            ) : "-"}
          </td>
          <td>{r.universal ? <span className="sbc-tag tag-online">UNIVERSAL</span> : <span className="sbc-tag">TENANT</span>}</td>
          <td>{r.enabled ? statusTag(r.status || "offline") : <span className="sbc-tag tag-offline">desativado</span>}</td>
          <td>{r.enabled ? loginTag(r) : <span className="sbc-tag tag-offline">desativado</span>}</td>
        </>;
      }}
      defaultValues={{
        nome: "", host: "", porta: 5060, protocolo: "udp",
        usuario: "", senha: "", codec: "PCMA,PCMU,G729",
        canais_max: 30, prefixo: "", remover_digitos_saida: "", incluir_digitos_saida: "", remover_prefixo_destino: "", adicionar_prefixo_destino: "", ips_autorizados: "",
        recording_enabled: false, universal: false, enabled: true,
      }}
      transformFormOpen={(form) => ({
        ...form,
        remover_digitos_saida: form.remover_digitos_saida || form.remover_prefixo_destino || "",
        incluir_digitos_saida: form.incluir_digitos_saida || form.adicionar_prefixo_destino || form.prefixo_saida || "",
        ips_autorizados: Array.isArray(form.ips_autorizados) ? form.ips_autorizados.join("\n") : "",
      })}
      transformSubmit={(form) => ({
        ...form,
        remover_prefixo_destino: form.remover_digitos_saida || "",
        adicionar_prefixo_destino: form.incluir_digitos_saida || "",
        ips_autorizados: String(form.ips_autorizados || "")
          .split(/[\n,;]+/)
          .map((value) => value.trim())
          .filter(Boolean),
      })}
      fields={[
        { key: "nome", label: "Nome", placeholder: "Ex: Vivo SIP Trunk", full: true },
        { key: "host", label: "Host / IP", mono: true, placeholder: "sip.vivo.com.br ou 200.150.10.5" },
        { key: "ips_autorizados", label: "IPs / CIDRs autorizados", type: "textarea", mono: true, full: true, rows: 4, placeholder: "Ex:\n177.11.50.217\n177.11.50.218/32", hint: "Aceita um ou mais IPs/CIDRs, um por linha, virgula ou ponto e virgula." },
        { key: "porta", label: "Porta", type: "number", mono: true },
        { key: "protocolo", label: "Protocolo", type: "select", options: [
          { value: "udp", label: "UDP" }, { value: "tcp", label: "TCP" }, { value: "tls", label: "TLS" }
        ]},
        { key: "usuario", label: "Usuário SIP", mono: true, placeholder: "opcional" },
        { key: "senha", label: "Senha SIP", mono: true, placeholder: "opcional" },
        { key: "codec", label: "Codecs (ordem)", mono: true, hint: "ex: PCMA,PCMU,G729", full: true },
        { key: "canais_max", label: "Canais máx", type: "number", mono: true },
        { key: "prefixo", label: "Prefixo (opcional)", mono: true, placeholder: "ex: 0011" },
        { key: "remover_digitos_saida", label: "Remover dígitos na saída", mono: true, placeholder: "ex: 55", hint: "Remove o DDI/prefixo recebido antes de enviar ao trunk. Para padrão nacional, use 55." },
        { key: "incluir_digitos_saida", label: "Incluir dígitos na saída", mono: true, placeholder: "ex: 0", hint: "Inclui o prefixo final da operadora sem duplicar. Para enviar 0 + DDD + número, use 0." },
        { key: "recording_enabled", label: "Gravar chamadas do tronco", type: "toggle" },
        ...(user?.role === "superadmin" ? [{ key: "universal", label: "Disponibilizar para todos os clientes", type: "toggle" }] : []),
        { key: "enabled", label: "Ativado", type: "toggle" },
      ]}
    />
  );
}
